import {
  type AdminClientJournalEntry,
  type AdminClientJournalRow,
  type LedgerMethod,
} from '@carsfinders/contracts';
import { useId } from 'react';

import { t } from '../../../../lib/i18n';
import { cn, formatMoney } from '../../../finance/utils';
import { formatChf, formatDate, formatSignedChf } from './finance-format';
import { cardClass, headingClass } from './finance-ui';

function amountTone(value: number): string {
  return value > 0 ? 'text-cf-success' : value < 0 ? 'text-cf-danger' : 'text-cf-ink';
}

const methodLabel = (method: LedgerMethod | null) =>
  method ? t(`clients.finance.journal.methods.${method}`) : null;

/** «EUR 1 000 ≈ CHF 1 052,63» — only for a payment received in another currency. */
function originalLine(entry: AdminClientJournalEntry): string | null {
  const original = entry.original;
  if (!original || original.currency === 'CHF') return null;
  return t('clients.finance.journal.original', {
    original: formatMoney(original.amount, original.currency),
    chf: formatChf(entry.totalChf),
  });
}

/**
 * Notes the API writes itself, as stored data in English (e.g.
 * `client-deposit.service.ts` on a refund). Mapped on display so rows already
 * in the ledger read right too; any other note is an operator's own words.
 */
const SYSTEM_NOTE_KEYS: Readonly<Record<string, string>> = {
  'deposit refunded to the client': 'clients.finance.journal.systemNotes.depositRefunded',
};

function noteText(note: string): string {
  const key = SYSTEM_NOTE_KEYS[note];
  return key ? t(key) : note;
}

/** Operator + reason line (adjustments carry the reason in `note`). */
function WhoWhy({ row }: { row: AdminClientJournalRow }) {
  if (!row.actorId && !row.note) return null;
  return (
    <p className="m-0 text-[12px] leading-[1.5] text-cf-ink-subtle">
      {row.note && t('clients.finance.journal.reason', { reason: noteText(row.note) })}
      {row.note && row.actorId && ' · '}
      {row.actorId && t('clients.finance.journal.by', { actor: row.actorName || row.actorId })}
    </p>
  );
}

function rowWhat(row: AdminClientJournalRow): string {
  const kind = t(`clients.finance.journal.kinds.${row.kind}`);
  if (!row.invoiceNo) return kind;
  const invoice = t('clients.finance.journal.invoice', { no: row.invoiceNo });
  return [kind, invoice, row.lotTitle].filter(Boolean).join(' · ');
}

/** One payment: its header (date, method, reference, total) and the allocations beneath. */
function PaymentEntry({ entry }: { entry: AdminClientJournalEntry }) {
  const original = originalLine(entry);
  const header = [
    t('clients.finance.journal.payment'),
    methodLabel(entry.method),
    entry.reference,
  ].filter(Boolean);
  return (
    <li
      data-testid={`finance-journal-${entry.paymentId ?? ''}`}
      className="flex flex-col gap-1.5 border-b border-cf-line-faint py-3 last:border-b-0"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-[13px] text-cf-ink">
          <span className="font-mono text-cf-ink-muted">{formatDate(entry.at)}</span>
          <span className="font-semibold">{header.join(' · ')}</span>
        </div>
        <span className={cn('font-mono text-[14px] font-semibold', amountTone(entry.totalChf))}>
          {formatSignedChf(entry.totalChf)}
        </span>
      </div>
      {original && <p className="m-0 font-mono text-[12px] text-cf-ink-muted">{original}</p>}
      <ul className="m-0 flex list-none flex-col gap-1 border-l-2 border-cf-line pl-3">
        {entry.rows.map((row) => (
          <li
            key={row.id}
            data-testid="finance-journal-row"
            className="flex flex-wrap items-baseline justify-between gap-2 text-[13px] text-cf-ink-muted"
          >
            <span>{rowWhat(row)}</span>
            <span className="font-mono">{formatChf(Math.abs(row.amountChf))}</span>
          </li>
        ))}
      </ul>
      {entry.rows[0] && <WhoWhy row={{ ...entry.rows[0], note: null }} />}
    </li>
  );
}

/** A movement that is not part of a payment: prepayment, adjustment, refund, fee, legacy top-up. */
function SingleEntry({
  entry,
  row,
}: {
  entry: AdminClientJournalEntry;
  row: AdminClientJournalRow;
}) {
  const details = [methodLabel(row.method), row.reference].filter(Boolean);
  return (
    <li
      data-testid={`finance-journal-${row.id}`}
      className="flex flex-col gap-1 border-b border-cf-line-faint py-3 last:border-b-0"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-[13px] text-cf-ink">
          <span className="font-mono text-cf-ink-muted">{formatDate(entry.at)}</span>
          <span className="font-semibold">{rowWhat(row)}</span>
          {details.length > 0 && <span className="text-cf-ink-muted">{details.join(' · ')}</span>}
        </div>
        <span className={cn('font-mono text-[14px] font-semibold', amountTone(row.amountChf))}>
          {formatSignedChf(row.amountChf)}
        </span>
      </div>
      <WhoWhy row={row} />
    </li>
  );
}

/**
 * «Рух грошей» — the client's money journal, newest first, straight from the
 * read model: a payment is ONE entry with its allocations beneath (grouped by
 * `paymentId` server-side), everything else — prepayments, adjustments with
 * their reason and actor, refunds — one line each. Dated by `at`, which is the
 * operator's «received» date for a payment.
 */
export function MoneyJournal({ journal }: { journal: AdminClientJournalEntry[] }) {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} data-testid="finance-journal" className={cardClass}>
      <h2 id={headingId} className={headingClass}>
        {t('clients.finance.journal.title')}
      </h2>
      {journal.length === 0 ? (
        <p className="m-0 text-[13px] text-cf-ink-subtle">{t('clients.finance.journal.empty')}</p>
      ) : (
        <ul className="m-0 flex list-none flex-col p-0">
          {journal.map((entry) =>
            entry.paymentId ? (
              <PaymentEntry key={entry.paymentId} entry={entry} />
            ) : (
              entry.rows.map((row) => <SingleEntry key={row.id} entry={entry} row={row} />)
            ),
          )}
        </ul>
      )}
    </section>
  );
}
